import type { ReactNode } from 'react';
import type { Pattern } from '@/api/client';
import { ago, compactCount, formatCount } from '@/lib/format';
import { excludePattern, keepPattern, PLACEHOLDER_LABEL, templateParts } from '@/lib/patterns';

export interface PatternListProps {
    patterns: Pattern[];
    total: number;
    q: string;
    now: number;
    loading: boolean;
    showRequests: boolean;
    // Runs a query: the pattern kept or excluded.
    onQuery: (q: string, lines: boolean) => void;
    onSample: (id: string) => void;
    empty: ReactNode;
}

// The lines grouped by their template, the biggest first: the shape of a range at a glance. Keep shows a pattern's
// lines, Exclude takes it out of the way (both are queries, readable in the query bar).
export function PatternList(props: PatternListProps): ReactNode {
    const max = Math.max(1, ...props.patterns.map((pattern) => pattern.count));
    if (props.patterns.length === 0) {
        return <div className="list">{props.loading ? <div className="empty">Grouping…</div> : props.empty}</div>;
    }

    return (
        <div className="list patterns">
            <div className="pattern-summary micro">
                <b>{props.patterns.length}</b> patterns in {formatCount(props.total)} lines{props.showRequests ? '' : ' · request dumps left out'}
            </div>
            {props.patterns.map((pattern) => {
                const keep = keepPattern(props.q, pattern.template);
                const exclude = excludePattern(props.q, pattern.template);
                const share = (pattern.count / Math.max(1, props.total)) * 100;

                return (
                    <div key={pattern.template} className={`prow lv-${pattern.level}`}>
                        <span className="pcount" title={formatCount(pattern.count)}>
                            {compactCount(pattern.count)}
                        </span>
                        <span className="pshare" title={`${share.toFixed(1)}% of the lines`}>
                            <b style={{ width: `${Math.max(2, (pattern.count / max) * 100)}%` }} />
                        </span>
                        <span className="ptemplate" onClick={() => keep && props.onQuery(keep, true)} title={keep ? 'Show these lines' : undefined}>
                            {templateParts(pattern.template).map((part, index) =>
                                part.kind === 'text' ? (
                                    <span key={index}>{part.text}</span>
                                ) : (
                                    <span key={index} className="slot">
                                        {PLACEHOLDER_LABEL[part.name] ?? part.name}
                                    </span>
                                ),
                            )}
                        </span>
                        <span className="pmeta mono">
                            {pattern.errors > 0 && (
                                <span className="perr">
                                    {compactCount(pattern.errors)} {pattern.errors === 1 ? 'error' : 'errors'}
                                </span>
                            )}
                            {pattern.warnings > 0 && (
                                <span className="pwarn">
                                    {compactCount(pattern.warnings)} {pattern.warnings === 1 ? 'warning' : 'warnings'}
                                </span>
                            )}
                            <span>{pattern.services.join(' · ')}</span>
                            <span className="pago">{ago(pattern.lastSeen, props.now)} ago</span>
                        </span>
                        <span className="pact">
                            <button disabled={!keep} onClick={() => keep && props.onQuery(keep, true)} title="Only these lines">
                                Keep
                            </button>
                            <button disabled={!exclude} onClick={() => exclude && props.onQuery(exclude, false)} title="Everything but these">
                                Exclude
                            </button>
                            <button onClick={() => props.onSample(pattern.sampleId)} title="Open the latest of them">
                                Open one
                            </button>
                        </span>
                    </div>
                );
            })}
        </div>
    );
}
